import {
  shouldInjectPixel,
  injectPixel,
  teardownPixel,
} from '~/utils/retargetingPixel'

/**
 * VK retargeting pixel loader (client-only) — loaded from the VENDOR'S CDN.
 *
 * The pixel `<script>` is served from scripts.prdredir.com (see
 * `utils/retargetingPixel.ts` for the URL + the security note): a product-owner
 * decision so the vendor's `slfp` cookie is set on the fetch. We run untrusted
 * third-party JS first-party; the ONLY guard is the guest gate below. Teardown
 * on login is best-effort residue cleanup, NOT a security boundary — it cannot
 * stop code the script already ran. This is an accepted risk.
 *
 * Product rules:
 *  - fires ONLY for anonymous (not-yet-authenticated) visitors,
 *  - only on the home route `/`,
 *  - never before the app's first page render has settled (see the #827 note
 *    on the decision gate below),
 *  - only while the runtime kill-switch is on (`marketing.retargetingEnabled`
 *    from /crash-config — admin-flippable in /config → Monitored scripts AND
 *    auto-disabled by the script-integrity monitor on CDN drift; no redeploy),
 *  - on EVERY such visit — fires once per page load, a reload re-fires (no
 *    once-per-browser cap),
 *  - and the moment a visitor authenticates, we stop injecting it and wipe our
 *    own residue — the `<script>` node + the first-party `_p_uid` cookie (best
 *    effort; it does not undo whatever the vendor script already executed).
 *
 * Enabled in PRODUCTION only, gated on the canonical prod host `PROD_HOST` — a
 * runtime hostname check, no env var. `apps/web` (the casino SPA) serves prod on
 * `aurumrun.io` (`aurumrun.com` / `www.aurumrun.io` 301 → it; `play.aurumrun.io`
 * is the separate landing app). Dev runs on `dev.aurumrun.com`, local on
 * `localhost`. Fails safe: any other host → pixel off, so staging/dev/local
 * never pollute the network's audience.
 *
 * Injection waits for `userStore.authInitialized`: on reload a returning user's
 * identity is painted optimistically and confirmed async (see
 * `plugins/auth.client.ts`), so we must not decide "guest" until auth has
 * settled — otherwise an authenticated user would momentarily fire the beacon
 * before teardown. The teardown watcher, by contrast, runs immediately and on
 * every `user` change, so the optimistic paint of a returning user clears any
 * stale residue right away.
 */
// Canonical production host of apps/web (the casino SPA). Verified 2026-08-10:
// aurumrun.com + www.aurumrun.io 301 → aurumrun.io, so the app itself only ever
// runs on aurumrun.io in prod; dev is dev.aurumrun.com, local is localhost.
const PROD_HOST = 'aurumrun.io'

export default defineNuxtPlugin({
  name: 'retargeting-pixel',
  parallel: true, // must never block other plugins
  setup() {
    // Production only (see PROD_HOST) — no env var; fails safe on any other host.
    if (window.location.hostname !== PROD_HOST) return

    const userStore = useUserStore()
    const route = useRoute()
    const { config, refresh } = useGameConfig()
    const nuxtApp = useNuxtApp()

    // Authenticated (now, on optimistic paint, or the instant they sign in) →
    // wipe every trace and never inject. Idempotent.
    watch(
      () => userStore.user,
      (u) => {
        if (u) teardownPixel()
      },
      { immediate: true },
    )

    // Inject once auth has SETTLED to a genuine guest, on `/`, with the runtime
    // kill-switch on. The latch keeps it to a single injection per page load (a
    // fresh load / reload re-fires — no once-per-browser cap).
    let decided = false
    const registerDecisionWatch = (): void => {
      watch(
        () => [
          userStore.authInitialized,
          !!userStore.user,
          route.path,
          config.value.marketing.retargetingEnabled,
        ] as const,
        ([authInitialized, authenticated, path, retargetingEnabled]) => {
          if (decided) return
          if (shouldInjectPixel({ authInitialized, authenticated, path, retargetingEnabled })) {
            decided = true
            injectPixel()
          }
        },
        { immediate: true },
      )
    }

    // Decide only once BOTH of these have happened:
    //   1. `/crash-config` was refreshed, so the kill-switch is honoured even
    //      when a returning visitor's localStorage holds a stale `enabled=true`;
    //   2. the app actually finished rendering its first page — `app:suspense:resolve`,
    //      which Nuxt fires when the root <Suspense> settles. Plugins run before
    //      `app:created` (and therefore before that first render), so the hook is
    //      always still pending at this point.
    //
    // (2) exists because of #827: a guest opening a guarded deep link (e.g.
    // `/bets-history`) is bounced to `/` by `middleware/auth` during `app:created`,
    // i.e. before the app has mounted. Deciding on the `refresh()` leg alone could
    // therefore fire the beacon against an unpainted shell while the document URL
    // still read the inner path — which is what made the requests look like they
    // belonged to that route in DevTools. Waiting for the render means the beacon always
    // fires with the home page actually rendered. It does not change who is
    // ELIGIBLE — a bounced guest still ends up on `/` and is still tagged —
    // provided they are still on the page when that render settles.
    //
    // Deliberately the raw hook rather than `onNuxtReady`: that helper appends an
    // unbounded `requestIdleCallback` on top of the same hook, which would queue
    // the beacon in the very tick the Pixi/Spine boot starts on `/` (the app's
    // heaviest route). A starved idle period would silently drop the beacon for
    // exactly the fast-bouncing mobile ad traffic this pixel exists to tag.
    //
    // Kept fire-and-forget so setup stays synchronous: Nuxt awaits `parallel: true`
    // plugins BEFORE the first render, so awaiting this promise would deadlock
    // boot. `allSettled` so neither leg failing can strand the watch; the trailing
    // `catch` covers only a throw from the immediate watch run. Fail-open — on a failed fetch `useGameConfig`
    // keeps its last-good/FALLBACK value, so the pixel still fires. A mid-session
    // flip to false takes effect on the next page load (the latch never un-injects
    // a live pixel).
    const firstRenderSettled = new Promise<void>((resolve) => {
      nuxtApp.hooks.hookOnce('app:suspense:resolve', () => resolve())
    })
    void Promise.allSettled([refresh(), firstRenderSettled])
      .then(registerDecisionWatch)
      // Only reachable if the immediate watch run throws — log it rather than
      // leaving an unhandled rejection with no trace.
      .catch((err) => { console.error('[retargeting] decision watch failed', err) })
  },
})
